import * as React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  AdminSidebar,
  type AdminSidebarNavItem,
} from '@dfq/shared/components/AdminSidebar';
import {
  AdminTopBar,
  type AdminTopBarUser,
} from '@dfq/shared/components/AdminTopBar';
import { SessionExpiryWarning } from '@dfq/shared/components/SessionExpiryWarning';
import { useHeartbeat, useLogout } from '@dfq/shared/hooks';
import { useMediaQuery } from '@dfq/shared/hooks/useMediaQuery';
import { cn } from '@dfq/shared/lib/utils';
import { useAppSelector } from '@dfq/shared/stores/hooks';

import { useQuery } from '@tanstack/react-query';

import {
  fetchOutstandingBalances,
  fetchOverCapturedOrders,
} from '@dfq/shared/api/admin/orders';

import { ADMIN_NAV_SECTIONS, buildBreadcrumb, type AdminNavSection } from './adminNav';

/** Scope guarding `GET /admin/orders/outstanding-balances`. */
const OUTSTANDING_BALANCES_PERMISSION = 'dashboard.payments.view';
import { NotificationBellContainer } from './NotificationBellContainer';

/**
 * CON-AUTH-001 — Console admin shell.
 *
 * Composes the shared {@link AdminSidebar} (CON-DS-002) and
 * {@link AdminTopBar} (CON-DS-003) capsules around a React Router
 * `<Outlet />`, and wires in the auth plumbing that every admin page
 * inherits at the shell layer:
 *
 *   1. Role-filtered nav — each nav section declares the roles and/or
 *      permission that gate it. The list is filtered once per render via
 *      `useHasAnyRole` / `usePermission`; sections the current user cannot
 *      access are hidden from the sidebar entirely (not just disabled).
 *   2. Session keep-alive — `useHeartbeat()` (SH-AUTH-003) fires while the
 *      tab is visible and the Redux slice reports `isAuthenticated`.
 *   3. Session-expiry warning — `<SessionExpiryWarning />` (SF-AUTH-008)
 *      renders outside `<Outlet />` so the countdown dialog survives route
 *      transitions within the admin shell.
 *   4. Sign-out — top-bar user menu wires `useLogout()` (SF-AUTH-009).
 *
 * Layout
 * - Left: `AdminSidebar` — 240px at `md+`, collapses to a 64px icon rail
 *   below `md`. Collapse state also persists to `localStorage` under
 *   `dfq-sidebar-collapsed` so manual toggles survive reloads.
 * - Right: a vertical column with `AdminTopBar` on top and a scrollable
 *   `<main>` hosting the routed page beneath it.
 *
 * Figma reference: `23:2` (Dashboard frame), `23:3` (sidebar),
 * `23:36` (top bar) — Console file `6wsm2RHMiHYoYcnW7Y38kL`.
 */

/** Media query for the "expanded sidebar" breakpoint (Tailwind `md`). */
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/** localStorage key persisting the user's manual collapse-toggle choice. */
const SIDEBAR_COLLAPSED_KEY = 'dfq-sidebar-collapsed';

/**
 * Hook — returns the filtered nav list the current session can see.
 *
 * Reads `roles` + `permissions` + `isAuthenticated` in a single selector
 * and applies each section's gate in plain TS. One selector → one
 * re-render on meaningful auth changes, and no hook-in-a-loop lint noise.
 *
 * Gate semantics:
 *   - No `requiredRoles` and no `requiredPermission` → always visible.
 *   - Otherwise the user must hold at least one `requiredRoles` role OR
 *     the `requiredPermission` string.
 *   - Unauthenticated sessions always see 0 gated sections — the slice
 *     guarantees `roles` / `permissions` are empty when logged out.
 */
function useVisibleNavSections(
  sections: readonly AdminNavSection[],
): ReadonlyArray<AdminSidebarNavItem> {
  const authed = useAppSelector((state) => state.auth.isAuthenticated);
  const roles = useAppSelector((state) => state.auth.roles);
  const permissions = useAppSelector((state) => state.auth.permissions);

  return React.useMemo(() => {
    return sections
      .filter((section) => {
        const hasGate =
          (section.requiredRoles && section.requiredRoles.length > 0) ||
          Boolean(section.requiredPermission);
        if (!hasGate) return true;
        if (!authed) return false;
        const roleOk =
          section.requiredRoles && section.requiredRoles.length > 0
            ? section.requiredRoles.some((r) => roles.includes(r))
            : false;
        const permOk = section.requiredPermission
          ? permissions.includes(section.requiredPermission)
          : false;
        return roleOk || permOk;
      })
      .map((section) => {
        // Strip the RBAC metadata before handing off to the shared capsule.
        const { requiredRoles, requiredPermission, ...rest } = section;
        void requiredRoles;
        void requiredPermission;
        return rest;
      });
  }, [sections, authed, roles, permissions]);
}

/**
 * Live count for the Orders badge — balance exceptions in both directions.
 *
 * An unrefunded overpayment needs an admin's attention as much as an
 * uncollected balance does, so the badge sums them; the tabs on the view
 * itself say which is which.
 *
 * Gated on the same permission as the view, so an admin who cannot open the
 * list never fires either request. `perPage: 1` because only
 * `meta.pagination.total` is wanted; the rows are thrown away.
 */
function useBalanceExceptionCount(): number {
  const canSee = useAppSelector((state) =>
    state.auth.permissions.includes(OUTSTANDING_BALANCES_PERMISSION),
  );

  const owedToUs = useQuery({
    queryKey: ['admin', 'outstanding-balances', 'count'],
    queryFn: () => fetchOutstandingBalances({ page: 1, perPage: 1 }),
    enabled: canSee,
    staleTime: 60_000,
  });

  const owedToCustomer = useQuery({
    queryKey: ['admin', 'over-captured', 'count'],
    queryFn: () => fetchOverCapturedOrders({ page: 1, perPage: 1 }),
    enabled: canSee,
    staleTime: 60_000,
  });

  return (owedToUs.data?.total ?? 0) + (owedToCustomer.data?.total ?? 0);
}

/** Derive the initial collapse value from localStorage + media-query. */
function readInitialCollapsed(isDesktop: boolean): boolean {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return !isDesktop;
  }
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // storage blocked — fall back to breakpoint
  }
  return !isDesktop;
}

export interface AdminLayoutProps {
  /** Optional className applied to the outer shell element. */
  className?: string;
  /**
   * Override for the nav sections — primarily a test knob so role-filtering
   * scenarios can be exercised with a minimal list. Defaults to the
   * canonical `ADMIN_NAV_SECTIONS`.
   */
  navSections?: readonly AdminNavSection[];
}

export function AdminLayout({
  className,
  navSections = ADMIN_NAV_SECTIONS,
}: AdminLayoutProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useAppSelector((state) => state.auth.user);

  // Sliding-window session keep-alive — mounted once at the shell so every
  // admin page inherits it without remounting the interval per view.
  useHeartbeat();

  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  // Manual collapse toggle state — persisted to localStorage so a user's
  // preference survives reloads. Auto-collapses below `md` regardless.
  const [manualCollapsed, setManualCollapsed] = React.useState<boolean>(() =>
    readInitialCollapsed(true /* assume desktop for SSR/first paint */),
  );

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(
        SIDEBAR_COLLAPSED_KEY,
        manualCollapsed ? 'true' : 'false',
      );
    } catch {
      // storage blocked — non-fatal
    }
  }, [manualCollapsed]);

  const effectiveCollapsed = !isDesktop || manualCollapsed;

  const toggleCollapsed = React.useCallback(() => {
    setManualCollapsed((prev) => !prev);
  }, []);

  const visibleNav = useVisibleNavSections(navSections);
  const balanceExceptionCount = useBalanceExceptionCount();

  const navWithBadges = React.useMemo(
    () =>
      visibleNav.map((item) =>
        item.id === 'orders' ? { ...item, badgeCount: balanceExceptionCount } : item,
      ),
    [visibleNav, balanceExceptionCount],
  );

  const breadcrumb = React.useMemo(
    () => buildBreadcrumb(location.pathname),
    [location.pathname],
  );

  const topBarUser = React.useMemo<AdminTopBarUser>(() => {
    if (user === null) return { name: 'Admin' };
    const combined = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return { name: combined === '' ? (user.email ?? 'Admin') : combined };
  }, [user]);

  const handleSignOut = React.useCallback(() => {
    void logout();
  }, [logout]);

  const handleProfile = React.useCallback(() => {
    navigate('/admin/settings/users');
  }, [navigate]);

  return (
    <div className={cn('flex h-screen overflow-hidden bg-background', className)}>
      <AdminSidebar
        collapsed={effectiveCollapsed}
        activePath={location.pathname}
        navItems={navWithBadges}
        className="sticky top-0 h-screen flex-shrink-0"
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AdminTopBar
          breadcrumb={breadcrumb}
          user={topBarUser}
          onSignOut={handleSignOut}
          onProfile={handleProfile}
          onSidebarToggle={isDesktop ? toggleCollapsed : undefined}
          sidebarCollapsed={effectiveCollapsed}
          notificationsSlot={<NotificationBellContainer />}
        />
        <main className="flex-1 overflow-y-auto px-6">
          <Outlet />
        </main>
      </div>

      {/* SF-AUTH-008 countdown dialog — OUTSIDE <Outlet /> so the dialog
          survives route transitions within the admin shell. Renders null
          while unauthenticated, so the shell stays quiet on first paint. */}
      <SessionExpiryWarning />
    </div>
  );
}
